import {
  InvalidateQueryFilters,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { SourceType, PageType } from 'api/page-template/types';
import {
  CreatePeoplePageTemplatePayload,
  GetTemporaryPeoplePageTemplatePayload,
  TemporaryPeoplePageTemplate,
  UpdatePeoplePageTemplatePayload,
} from 'api/people-page-template/types';
import {
  PeoplePageTemplate,
  PeoplePageTemplateLite,
} from 'api/people-page-template/types';
import {
  CreatePageTemplateMutationOptions,
  GetAllPageTemplateQueryOption,
  GetPageTemplateQueryOption,
  UpdatePageTemplateMutationOptions,
  useCreatePageTemplateMutation,
  useDeletePageTemplateMutation,
  useGetAllPageTemplatesQuery,
  useGetPageTemplateQuery,
  useInvalidateAllPageTemplatesQuery,
  useInvalidatePageTemplateQuery,
  useUpdatePageTemplateMutation,
  useUpdatePageTemplateOrderMutation,
  useUpdatePageTemplateStatusMutation,
} from 'queries/templates';
import { useCallback } from 'react';

export const useGetAllPeoplePageTemplatesQuery = (
  queryOptions?: GetAllPageTemplateQueryOption<
    PageType.People,
    SourceType.CustomerUser
  >,
): UseQueryResult<PeoplePageTemplateLite[], Error> =>
  useGetAllPageTemplatesQuery(
    PageType.People,
    SourceType.CustomerUser,
    queryOptions,
  );

export const useGetPeoplePageTemplateQuery = (
  peoplePageTemplateId: number,
  queryOptions?: GetPageTemplateQueryOption<
    PageType.People,
    SourceType.CustomerUser
  >,
): UseQueryResult<PeoplePageTemplate, Error> =>
  useGetPageTemplateQuery(peoplePageTemplateId, queryOptions);

const useInvalidatePeoplePageTemplatesHandler = () => {
  const invalidateAllPageTemplates = useInvalidateAllPageTemplatesQuery(
    PageType.People,
    SourceType.CustomerUser,
  );

  return useCallback(
    async (options?: InvalidateQueryFilters) => {
      await invalidateAllPageTemplates(options);
    },
    [invalidateAllPageTemplates],
  );
};

export const useCreatePeoplePageTemplateMutation = (
  options?: Omit<
    CreatePageTemplateMutationOptions<PageType.People, SourceType.CustomerUser>,
    'onSuccess'
  >,
): UseMutationResult<
  PeoplePageTemplate,
  Error,
  CreatePeoplePageTemplatePayload
> => {
  const invalidateAllPeoplePages = useInvalidatePeoplePageTemplatesHandler();

  return useCreatePageTemplateMutation<
    PageType.People,
    SourceType.CustomerUser
  >({
    onSuccess: () =>
      invalidateAllPeoplePages({
        refetchType: 'all',
      }),
    ...options,
  });
};

export const useUpdatePeoplePageTemplateMutation = (
  options?: UpdatePageTemplateMutationOptions<
    PageType.People,
    SourceType.CustomerUser
  >,
): UseMutationResult<
  PeoplePageTemplate,
  Error,
  UpdatePeoplePageTemplatePayload
> => {
  const invalidateAllPageTemplates = useInvalidateAllPageTemplatesQuery(
    PageType.People,
    SourceType.CustomerUser,
  );
  const invalidatePageTemplate = useInvalidatePageTemplateQuery();

  return useUpdatePageTemplateMutation<
    PageType.People,
    SourceType.CustomerUser
  >({
    onSuccess: async (_, data) => {
      await invalidateAllPageTemplates({
        refetchType: 'all',
      });
      await invalidatePageTemplate(data.templateId);
    },
    ...options,
  });
};

export const useDeletePeoplePageTemplateMutation = (
  queryOptions?: Omit<
    UseMutationOptions<void, Error, number>,
    'mutationFn' | 'onSuccess'
  >,
): UseMutationResult<void, Error, number> => {
  const invalidateAllPeoplePages = useInvalidatePeoplePageTemplatesHandler();

  return useDeletePageTemplateMutation({
    onSuccess: () => invalidateAllPeoplePages(),
    ...queryOptions,
  });
};

export const useUpdatePeoplePageTemplateOrderMutation = (
  queryOptions?: Omit<
    UseMutationOptions<void, Error, { templateId: number; order: number }>,
    'mutationFn' | 'onSuccess'
  >,
): UseMutationResult<void, Error, { templateId: number; order: number }> => {
  const invalidateAllPeoplePages = useInvalidatePeoplePageTemplatesHandler();

  return useUpdatePageTemplateOrderMutation({
    onSuccess: () => invalidateAllPeoplePages(),
    ...queryOptions,
  });
};

export const useUpdatePeoplePageTemplateStatusMutation = (
  queryOptions?: Omit<
    UseMutationOptions<void, Error, { templateId: number; status: boolean }>,
    'mutationFn' | 'onSuccess'
  >,
): UseMutationResult<void, Error, { templateId: number; status: boolean }> => {
  const invalidateAllPeoplePages = useInvalidatePeoplePageTemplatesHandler();

  return useUpdatePageTemplateStatusMutation({
    onSuccess: () => invalidateAllPeoplePages(),
    ...queryOptions,
  });
};
const TEMPORARY_PEOPLE_PAGE_TEMPLATE_KEY = [
  'page-template',
  PageType.People,
  'new',
];

export const useCreateTemporaryPeoplePageTemplateMutation =
  (): UseMutationResult<
    TemporaryPeoplePageTemplate,
    Error,
    GetTemporaryPeoplePageTemplatePayload
  > => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (payload: GetTemporaryPeoplePageTemplatePayload) => {
        const response = {
          ...payload,
        };

        queryClient.setQueryDefaults(TEMPORARY_PEOPLE_PAGE_TEMPLATE_KEY, {
          staleTime: Infinity,
          gcTime: Infinity,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
        });
        queryClient.setQueryData<TemporaryPeoplePageTemplate>(
          TEMPORARY_PEOPLE_PAGE_TEMPLATE_KEY,
          response,
        );

        return response;
      },
    });
  };
export const useGetTemporaryPeoplePageTemplate = () => {
  const queryClient = useQueryClient();

  return queryClient.getQueryData<TemporaryPeoplePageTemplate>(
    TEMPORARY_PEOPLE_PAGE_TEMPLATE_KEY,
  );
};
